import type { PendingApproval } from '../../types/runtime';
import { useLexRuntimeStore } from '../../store/lexRuntimeStore';

interface Props {
  approval: PendingApproval;
}

export function ApprovalRequestCard({ approval }: Props) {
  const isLoading = useLexRuntimeStore((s) => s.isLoading);
  const resume = useLexRuntimeStore((s) => s.resumeWithDecision);

  const payload = approval.payload as Record<string, unknown>;
  const taskTitle = typeof payload.title === 'string' ? payload.title : null;
  const matterLabel =
    typeof payload.matter_description === 'string'
      ? payload.matter_description
      : typeof payload.matter_number === 'string'
        ? payload.matter_number
        : null;

  return (
    <article className="rounded-lg border-2 border-violet-300 bg-violet-50 p-4 text-sm text-violet-900 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded bg-violet-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-900">
          Approval required
        </span>
        <span className="text-xs text-violet-700">{approval.action_kind}</span>
      </div>
      <p className="mb-3 font-medium leading-relaxed">{approval.summary}</p>
      {(taskTitle || matterLabel) && (
        <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          {taskTitle && (
            <>
              <dt className="font-semibold">Task:</dt>
              <dd>{taskTitle}</dd>
            </>
          )}
          {matterLabel && (
            <>
              <dt className="font-semibold">Matter:</dt>
              <dd>{matterLabel}</dd>
            </>
          )}
        </dl>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={isLoading}
          onClick={() => resume('approved', 'user_clicked_approve')}
          className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Approve
        </button>
        <button
          type="button"
          disabled={isLoading}
          onClick={() => resume('rejected', 'user_clicked_reject')}
          className="rounded border border-violet-300 bg-white px-3 py-1.5 text-xs font-semibold text-violet-900 shadow-sm hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reject
        </button>
      </div>
      <p className="mt-3 text-[11px] text-violet-700">
        This action mutates data. Nothing happens until you choose. The decision
        is recorded in the action audit log either way.
      </p>
    </article>
  );
}
